import React, { useState, useMemo } from 'react';
import { 
  Users, 
  Search, 
  Filter, 
  Plus, 
  Edit, 
  Eye, 
  Building, 
  Mail, 
  Phone, 
  MapPin, 
  DollarSign, 
  TrendingUp, 
  Calendar, 
  Star,
  ArrowLeft,
  Activity
} from 'lucide-react';
import { customers } from '../../data/salesCycleData';

const CustomerManagement = ({ onBack, navigationSource }) => {
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [industryFilter, setIndustryFilter] = useState('all');
  const [sortField, setSortField] = useState('company_name');
  const [sortDirection, setSortDirection] = useState('asc');

  // Helper functions
  const getIndustryColor = (industry) => {
    const colors = {
      'electronics': 'bg-blue-100 text-blue-800',
      'technology': 'bg-purple-100 text-purple-800',
      'manufacturing': 'bg-green-100 text-green-800',
      'automotive': 'bg-red-100 text-red-800',
      'aerospace': 'bg-indigo-100 text-indigo-800'
    };
    return colors[industry] || 'bg-gray-100 text-gray-800';
  };

  const getCreditRatingColor = (rating) => {
    const colors = {
      'A+': 'bg-green-100 text-green-800',
      'A': 'bg-blue-100 text-blue-800',
      'B+': 'bg-yellow-100 text-yellow-800',
      'B': 'bg-orange-100 text-orange-800',
      'C': 'bg-red-100 text-red-800'
    };
    return colors[rating] || 'bg-gray-100 text-gray-800';
  };

  const formatCurrency = (amount, currency = 'TWD') => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Filtering and sorting
  const filteredCustomers = useMemo(() => {
    return customers.filter(customer => {
      if (statusFilter !== 'all' && customer.status !== statusFilter) {
        return false;
      }
      if (industryFilter !== 'all' && customer.industry !== industryFilter) {
        return false;
      }
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          customer.company_name.toLowerCase().includes(searchLower) ||
          customer.customer_code.toLowerCase().includes(searchLower) ||
          customer.industry_name.toLowerCase().includes(searchLower)
        );
      }
      return true;
    });
  }, [statusFilter, industryFilter, searchTerm]);

  const sortedCustomers = useMemo(() => {
    return [...filteredCustomers].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'company_name':
          comparison = a.company_name.localeCompare(b.company_name);
          break;
        case 'credit_rating':
          comparison = a.credit_rating.localeCompare(b.credit_rating);
          break;
        case 'annual_revenue':
          comparison = a.annual_revenue - b.annual_revenue;
          break;
        case 'total_revenue':
          comparison = a.sales_history.total_revenue - b.sales_history.total_revenue;
          break;
        default:
          comparison = 0;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredCustomers, sortField, sortDirection]);

  const renderWorkflowBanner = () => {
    if (navigationSource === 'sales-workflow') {
      return (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-4 mb-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-500 p-2 rounded-full">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-blue-800">來自業務流程: 銷售週期管理</h3>
                <p className="text-sm text-blue-600">階段A: 潛在客戶識別與開發 - 客戶管理</p>
              </div>
            </div>
            <button
              onClick={onBack}
              className="flex items-center space-x-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>返回銷售週期</span>
            </button>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderDashboard = () => {
    const totalCustomers = customers.length;
    const activeCustomers = customers.filter(c => c.status === 'active').length;
    const totalRevenue = customers.reduce((sum, c) => sum + c.sales_history.total_revenue, 0);
    const avgOrderValue = customers.reduce((sum, c) => sum + c.sales_history.avg_order_value, 0) / customers.length;

    return (
      <div className="space-y-6">
        {renderWorkflowBanner()}
        
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">客戶管理儀表板</h2>
          <button
            onClick={() => setActiveView('create')}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>新增客戶</span>
          </button>
        </div>

        {/* 統計卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">客戶總數</p>
                <p className="text-2xl font-bold text-gray-800">{totalCustomers}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">活躍客戶</p>
                <p className="text-2xl font-bold text-gray-800">{activeCustomers}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">總營收</p>
                <p className="text-2xl font-bold text-gray-800">{formatCurrency(totalRevenue)}</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-full">
                <DollarSign className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">平均訂單價值</p>
                <p className="text-2xl font-bold text-gray-800">{formatCurrency(avgOrderValue)}</p>
              </div>
              <div className="bg-yellow-100 p-3 rounded-full">
                <Star className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>
        </div>

        {/* 客戶列表 */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">客戶列表</h3>
              <button
                onClick={() => setActiveView('list')}
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                查看全部
              </button>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {customers.slice(0, 5).map((customer) => (
                <div
                  key={customer.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => {
                    setSelectedCustomer(customer);
                    setActiveView('detail');
                  }}
                >
                  <div className="flex items-center space-x-4">
                    <div className="bg-blue-100 p-3 rounded-full">
                      <Building className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800">{customer.company_name}</h4>
                      <p className="text-sm text-gray-600">{customer.customer_code}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="font-semibold text-gray-800">
                        {formatCurrency(customer.sales_history.total_revenue, customer.currency)}
                      </p>
                      <p className="text-sm text-gray-600">總營收</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCreditRatingColor(customer.credit_rating)}`}>
                      {customer.credit_rating}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderList = () => (
    <div className="space-y-6">
      {renderWorkflowBanner()}
      
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">客戶列表</h2>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setActiveView('create')}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>新增客戶</span>
          </button>
          <button
            onClick={() => setActiveView('dashboard')}
            className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>返回儀表板</span>
          </button>
        </div>
      </div>

      {/* 搜尋和篩選 */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="搜尋客戶..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">所有狀態</option>
            <option value="active">活躍</option>
            <option value="inactive">非活躍</option>
          </select>
          
          <select
            value={industryFilter}
            onChange={(e) => setIndustryFilter(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">所有行業</option>
            <option value="electronics">電子製造業</option>
            <option value="technology">科技服務業</option>
            <option value="manufacturing">製造業</option>
          </select>
          
          <select
            value={sortField}
            onChange={(e) => setSortField(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="company_name">公司名稱</option>
            <option value="credit_rating">信用評等</option>
            <option value="annual_revenue">年營收</option>
            <option value="total_revenue">總營收</option>
          </select>
        </div>
      </div>

      {/* 客戶表格 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                客戶資訊
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                行業
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                信用評等
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                總營收
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                狀態
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                操作
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedCustomers.map((customer) => (
              <tr key={customer.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="bg-blue-100 p-2 rounded-full mr-3">
                      <Building className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{customer.company_name}</div>
                      <div className="text-sm text-gray-500">{customer.customer_code}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getIndustryColor(customer.industry)}`}>
                    {customer.industry_name}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCreditRatingColor(customer.credit_rating)}`}>
                    {customer.credit_rating}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatCurrency(customer.sales_history.total_revenue, customer.currency)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    customer.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {customer.status === 'active' ? '活躍' : '非活躍'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => {
                      setSelectedCustomer(customer);
                      setActiveView('detail');
                    }}
                    className="text-blue-600 hover:text-blue-900 mr-3"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      setSelectedCustomer(customer);
                      setActiveView('edit');
                    }}
                    className="text-green-600 hover:text-green-900"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderDetail = () => {
    if (!selectedCustomer) return null;

    return (
      <div className="space-y-6">
        {renderWorkflowBanner()}
        
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">客戶詳細資訊</h2>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setActiveView('edit')}
              className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
            >
              <Edit className="w-4 h-4" />
              <span>編輯客戶</span>
            </button>
            <button
              onClick={() => setActiveView('list')}
              className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>返回列表</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 基本資訊 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">基本資訊</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Building className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="font-medium text-gray-800">{selectedCustomer.company_name}</p>
                  <p className="text-sm text-gray-600">{selectedCustomer.company_name_en}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <span className="w-5 h-5 flex items-center justify-center text-gray-400">#</span>
                <div>
                  <p className="font-medium text-gray-800">{selectedCustomer.customer_code}</p>
                  <p className="text-sm text-gray-600">客戶代碼</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <span className="w-5 h-5 flex items-center justify-center text-gray-400">🏭</span>
                <div>
                  <p className="font-medium text-gray-800">{selectedCustomer.industry_name}</p>
                  <p className="text-sm text-gray-600">行業別</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Calendar className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="font-medium text-gray-800">{selectedCustomer.established_date}</p>
                  <p className="text-sm text-gray-600">成立日期</p>
                </div>
              </div>
            </div>
          </div>

          {/* 聯絡資訊 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">聯絡資訊</h3>
            <div className="space-y-4">
              {selectedCustomer.contacts.map((contact) => (
                <div key={contact.id} className="border-b border-gray-200 pb-4 last:border-b-0">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium text-gray-800">{contact.name}</p>
                    {contact.is_primary && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                        主要聯絡人
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{contact.title} - {contact.department}</p>
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <div className="flex items-center space-x-1">
                      <Mail className="w-4 h-4" />
                      <span>{contact.email}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Phone className="w-4 h-4" />
                      <span>{contact.phone}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 財務資訊 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">財務資訊</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">信用評等</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCreditRatingColor(selectedCustomer.credit_rating)}`}>
                  {selectedCustomer.credit_rating}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">信用額度</span>
                <span className="font-medium text-gray-800">
                  {formatCurrency(selectedCustomer.credit_limit, selectedCustomer.currency)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">付款條件</span>
                <span className="font-medium text-gray-800">{selectedCustomer.payment_terms}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">年營收</span>
                <span className="font-medium text-gray-800">
                  {formatCurrency(selectedCustomer.annual_revenue, selectedCustomer.currency)}
                </span>
              </div>
            </div>
          </div>

          {/* 銷售歷史 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">銷售歷史</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">總訂單數</span>
                <span className="font-medium text-gray-800">{selectedCustomer.sales_history.total_orders}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">總營收</span>
                <span className="font-medium text-gray-800">
                  {formatCurrency(selectedCustomer.sales_history.total_revenue, selectedCustomer.currency)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">平均訂單價值</span>
                <span className="font-medium text-gray-800">
                  {formatCurrency(selectedCustomer.sales_history.avg_order_value, selectedCustomer.currency)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">今年營收</span>
                <span className="font-medium text-gray-800">
                  {formatCurrency(selectedCustomer.sales_history.ytd_revenue, selectedCustomer.currency)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeView) {
      case 'dashboard':
        return renderDashboard();
      case 'list':
        return renderList();
      case 'detail':
        return renderDetail();
      case 'create':
        return (
          <div className="space-y-6">
            {renderWorkflowBanner()}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">新增客戶</h2>
              <p className="text-gray-600">客戶新增功能開發中...</p>
              <button
                onClick={() => setActiveView('dashboard')}
                className="mt-4 flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>返回儀表板</span>
              </button>
            </div>
          </div>
        );
      case 'edit':
        return (
          <div className="space-y-6">
            {renderWorkflowBanner()}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">編輯客戶</h2>
              <p className="text-gray-600">客戶編輯功能開發中...</p>
              <button
                onClick={() => setActiveView('detail')}
                className="mt-4 flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>返回詳細資訊</span>
              </button>
            </div>
          </div>
        );
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {renderContent()}
      </div>
    </div>
  );
};

export default CustomerManagement; 